"use client";

/**
 * The contact page's body: the form or an open conversation on the left,
 * the one side card with the account's conversations on the right.
 *
 * The account's conversations with us, on the contact page (owner,
 * 2026-10-26: "if a user clicked out of the conversation, they should
 * find it again: the active ones, the ones expecting a reply from us,
 * the ones expecting something from them"; round 2: "filters for the
 * open and the closed ones; opened from here it should not be a modal,
 * something bigger, and no collapsed messages").
 *
 * The list with its filter pills sits above the form. A row opens the
 * conversation in place of the list and the form (full column, the URL
 * carries ?conversation=<id>, the back link and the browser's back both
 * return to the list): every message in order, the original first, the
 * reply box at the bottom. The corner card keeps its sheet for a quick
 * reply from any other page. Nothing renders for a visitor or an account
 * that never wrote: the children (the form) show as they are.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { SELECTED, UNREAD_DOT, UNSELECTED } from "@/lib/ui-classes";
import { usePathname, useSearchParams } from "next/navigation";
import { ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useLocale, useT } from "@/lib/locale-context";
import { useLocalizedRouter } from "@/lib/localized-router";
import type { TranslationKey } from "@/lib/i18n";
import {
  THREAD_CHANGED_EVENT, loadThread, loadThreads, replyThread,
  type ThreadState, type ThreadStateName, type ThreadSummary,
} from "@/lib/feedback-thread";
import { trackEvent } from "@/lib/tracking";
import { dateLabel } from "@/components/FeedbackOffer";
import ContactAside from "@/components/ContactAside";
import ContactForm from "@/components/ContactForm";

const STATE_LABEL: Record<ThreadStateName, TranslationKey> = {
  awaiting_user: "contact.conv.state.awaiting_user",
  awaiting_team: "contact.conv.state.awaiting_team",
  answered: "contact.conv.state.answered",
  closed: "contact.conv.state.closed",
};
// Colour semantics (DESIGN.md): amber warns (your reply is due), emerald
// passes (answered), gray waits or is over.
const STATE_TONE: Record<ThreadStateName, string> = {
  awaiting_user: "bg-amber-50 text-amber-800",
  awaiting_team: "bg-gray-100 text-gray-600",
  answered: "bg-emerald-50 text-emerald-700",
  closed: "bg-gray-100 text-gray-500",
};
const CATEGORY_KEYS: Record<string, TranslationKey> = {
  feedback: "contact.form.category.feedback",
  bug: "contact.form.category.bug",
  feature: "contact.form.category.feature",
  question: "contact.form.category.question",
  other: "contact.form.category.other",
};
type Show = "all" | "open" | "closed";
const SHOW_LABEL: Record<Show, TranslationKey> = {
  all: "contact.conv.filter.all",
  open: "contact.conv.filter.open",
  closed: "contact.conv.filter.closed",
};
const PARAM = "conversation";

export default function ContactBody() {
  const t = useT();
  const { locale } = useLocale();
  const { user } = useAuth();
  const router = useLocalizedRouter();
  const pathname = usePathname();
  const search = useSearchParams();
  const [threads, setThreads] = useState<ThreadSummary[] | null>(null);
  const [show, setShow] = useState<Show>("open");
  const openId = Number(search.get(PARAM)) || null;

  useEffect(() => {
    if (!user) { setThreads(null); return undefined; }
    let alive = true;
    const read = () => loadThreads().then((s) => {
      if (!alive) return;
      setThreads(s.threads);
      // Open ones by default (owner, 2026-10-26); an account with only
      // closed ones sees them rather than an empty list.
      if (!s.threads.some((t) => t.state !== "closed")) setShow("all");
    }).catch(() => {});
    read();
    window.addEventListener(THREAD_CHANGED_EVENT, read);
    return () => { alive = false; window.removeEventListener(THREAD_CHANGED_EVENT, read); };
  }, [user]);

  // The open conversation lives in the URL: the back link, the browser's
  // back and a shared link all land on the same view.
  const setOpen = useCallback((id: number | null) => {
    const params = new URLSearchParams(search.toString());
    if (id) params.set(PARAM, String(id)); else params.delete(PARAM);
    const qs = params.toString();
    router.push(`${pathname?.replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/contact"}${qs ? `?${qs}` : ""}`, { scroll: false });
  }, [router, pathname, search]);

  const has = !!user && !!threads && threads.length > 0;
  const open = has && openId ? threads!.find((s) => s.id === openId) || null : null;
  const dayLabel = (iso: string) => dateLabel(iso.slice(0, 10), locale);
  const shown = (threads || []).filter((s) => show === "all" || (show === "open" ? s.state !== "closed" : s.state === "closed"));
  const pill = (on: boolean) =>
    `inline-flex h-8 items-center rounded-lg border px-2.5 text-xs font-semibold transition-colors ${
      on ? SELECTED : UNSELECTED
    }`;
  const pick = (s: ThreadSummary) => {
    trackEvent("contact.conv.open", { state: s.state, unread: s.unread });
    setOpen(s.id);
  };

  const list = has ? (
    <div id="conversations" aria-labelledby="conversations-title">
      <h2 id="conversations-title" className="text-sm font-bold text-gray-900">{t("contact.conv.title")}</h2>
      <div className="mt-2.5 flex flex-wrap gap-1.5" role="tablist">
        {(Object.keys(SHOW_LABEL) as Show[]).map((s) => (
          <button key={s} type="button" role="tab" aria-selected={show === s} onClick={() => setShow(s)} className={pill(show === s)}>
            {t(SHOW_LABEL[s])}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">{t("contact.conv.filter.empty")}</p>
      ) : (
        <ul className="mt-1 divide-y divide-gray-100">
          {shown.map((s) => {
            const current = openId === s.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => pick(s)}
                  aria-current={current ? "true" : undefined}
                  aria-label={`${t("contact.conv.open")}: ${s.subject}`}
                  className={`group flex w-full items-start justify-between gap-2 rounded-lg py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${current ? "text-gray-900" : "hover:text-gray-900"}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      {s.unread > 0 && <span className={`h-2 w-2 shrink-0 rounded-full ${UNREAD_DOT}`} aria-label={t("contact.conv.unread")} />}
                      <span className={`truncate text-sm ${s.unread > 0 ? "font-bold" : "font-semibold"} text-gray-900 ${current ? "underline underline-offset-2" : ""}`}>{s.subject}</span>
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                      <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${STATE_TONE[s.state]}`}>{t(STATE_LABEL[s.state])}</span>
                      <span>{s.last_at ? dayLabel(s.last_at) : ""}</span>
                    </span>
                  </span>
                  <ChevronRight className={`mt-1 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${current ? "text-gray-900" : "text-gray-400"}`} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <section>
        {has && openId ? (
          <ConversationView id={openId} summary={open} onBack={() => setOpen(null)} />
        ) : (
          <ContactForm />
        )}
      </section>
      <ContactAside conversations={list} first={has} />
    </div>
  );
}

/** One conversation, whole: the original message first, every reply in
 *  order, the box at the bottom. Opening it marks the team's messages as
 *  read on the server. */
function ConversationView({ id, summary, onBack }: { id: number; summary: ThreadSummary | null; onBack: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const [thread, setThread] = useState<ThreadState | null>(null);
  const [missing, setMissing] = useState(false);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<"form" | "sending" | "sent">("form");
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setThread(null); setMissing(false);
    loadThread(true, id)
      .then((s) => { if (!alive) return; if (s.ticket) setThread(s); else setMissing(true); })
      .catch(() => { if (alive) setMissing(true); });
    return () => { alive = false; };
  }, [id]);
  // A long conversation opens at its latest message and the box (owner:
  // "if the conversation is really long, make sure we scroll to the bottom").
  useEffect(() => {
    if (!thread) return;
    const id = window.requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: "end" }));
    return () => window.cancelAnimationFrame(id);
  }, [thread?.ticket?.id, thread?.messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = text.trim().length >= 2;
  const send = async () => {
    if (!ready || !thread?.ticket) return;
    setPhase("sending"); setError(null);
    try {
      const next = await replyThread(thread.ticket.id, text.trim());
      trackEvent("contact.conv.reply", { chars: text.trim().length, state: summary?.state });
      setThread(next); setText(""); setPhase("sent");
    } catch {
      setError(t("fbo.err.generic")); setPhase("form");
    }
  };

  const state = summary?.state;
  const when = (iso: string) => dateLabel(iso.slice(0, 10), locale);
  const back = (
    <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 underline decoration-gray-300 hover:decoration-gray-900 underline-offset-4">
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t("contact.conv.back")}
    </button>
  );

  return (
    <section aria-labelledby="conversation-title" className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 card-rise">
      {back}
      {missing && <p className="mt-4 text-sm text-gray-500">{t("contact.conv.missing")}</p>}
      {!thread && !missing && (
        <p className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />{t("demand.loading")}</p>
      )}
      {thread?.ticket && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1">
            <h2 id="conversation-title" className="text-lg font-bold tracking-tight text-gray-900">{thread.ticket.subject}</h2>
            {state && <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${STATE_TONE[state]}`}>{t(STATE_LABEL[state])}</span>}
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            {CATEGORY_KEYS[thread.ticket.category] ? `${t(CATEGORY_KEYS[thread.ticket.category])} · ` : ""}{when(thread.ticket.created_at)}
          </p>
          <ol className="mt-5 space-y-3">
            <li className="flex justify-end">
              <Bubble mine who={t("contact.conv.you")} date={when(thread.ticket.created_at)}>{thread.ticket.message}</Bubble>
            </li>
            {thread.messages.map((m) => (
              <li key={m.id} className={`flex ${m.author === "user" ? "justify-end" : "justify-start"}`}>
                <Bubble mine={m.author === "user"} who={m.author === "user" ? t("contact.conv.you") : t("contact.conv.team")} date={when(m.created_at)}>
                  {m.body}
                </Bubble>
              </li>
            ))}
          </ol>
          <div className="mt-5 space-y-2 border-t border-gray-200 pt-4">
            {state === "closed" && <p className="text-xs text-gray-500">{t("contact.conv.reopen_hint")}</p>}
            {error && <p className="text-sm text-rose-600" role="alert">{error}</p>}
            <textarea
              value={text}
              onChange={(e) => { setText(e.target.value); if (phase === "sent") setPhase("form"); }}
              rows={3}
              maxLength={1500}
              placeholder={t("fbt.placeholder")}
              aria-label={t("fbt.placeholder")}
              className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm leading-6 text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-emerald-600">{phase === "sent" ? t("fbt.sent") : ""}</span>
              <button
                type="button"
                onClick={send}
                disabled={!ready || phase === "sending"}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {phase === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                {t("fbt.send")}
              </button>
            </div>
            <div ref={endRef} />
          </div>
        </>
      )}
    </section>
  );
}

function Bubble({ mine, who, date, children }: { mine: boolean; who: string; date: string; children: React.ReactNode }) {
  return (
    <div className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${mine ? "bg-gray-100 text-gray-900" : "border border-gray-200 bg-white text-gray-900"}`}>
      <p className="mb-0.5 flex items-center gap-2 text-[11px] font-semibold text-gray-500">
        <span>{who}</span><span className="font-normal">{date}</span>
      </p>
      {children}
    </div>
  );
}
